import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Card, CardContent } from "../../../../components/ui/card";
import { useCheckoutContext } from "../CheckoutContext";
import { CustomerInfoFormData, customerInfoSchema } from "../schemas";
import { customerInfoResolver } from "../resolvers";
import { ControlledInput } from "./ControlledInput";

export const CustomerInformation: React.FC = () => {
  const { state, updateCustomerInfo, registerCustomerInfoValidation } = useCheckoutContext();

  const {
    control,
    watch,
    trigger,
    setValue,
    setError,
    clearErrors,
  } = useForm<CustomerInfoFormData>({
    resolver: customerInfoResolver,
    defaultValues: {
      firstName: state.customerInfo.firstName,
      lastName: state.customerInfo.lastName,
      email: state.customerInfo.email,
      phone: state.customerInfo.phone,
    },
    mode: 'onChange', // Validação em tempo real
    criteriaMode: 'all', // Mostrar todos os erros
  });

  // Sincronizar dados do formulário com o contexto em tempo real
  // Usar callback do watch para evitar re-renders excessivos
  useEffect(() => {
    const subscription = watch((formData) => {
      // Só atualiza se todos os campos estiverem definidos
      if (formData.firstName !== undefined && 
          formData.lastName !== undefined && 
          formData.email !== undefined && 
          formData.phone !== undefined) {
        updateCustomerInfo(formData as CustomerInfoFormData);
      }
    });
    
    return () => subscription.unsubscribe();
  }, [watch, updateCustomerInfo]);

  // Registrar função de validação no contexto
  useEffect(() => {
    registerCustomerInfoValidation(async () => {
      try {
        // Sincronizar valores do contexto com o formulário antes de validar
        setValue('firstName', state.customerInfo.firstName, { shouldValidate: false, shouldTouch: true });
        setValue('lastName', state.customerInfo.lastName, { shouldValidate: false, shouldTouch: true });
        setValue('email', state.customerInfo.email, { shouldValidate: false, shouldTouch: true });
        setValue('phone', state.customerInfo.phone, { shouldValidate: false, shouldTouch: true });
        
        // Validar com Zod diretamente para obter erros detalhados
        const validationResult = customerInfoSchema.safeParse(state.customerInfo);
        
        if (!validationResult.success) {
          // Limpar erros anteriores
          clearErrors();
          
          // Definir erros manualmente baseado na validação do Zod
          validationResult.error.issues.forEach((issue) => {
            const fieldName = issue.path[0] as keyof CustomerInfoFormData;
            if (fieldName) {
              setError(fieldName, {
                type: 'manual',
                message: issue.message,
              });
            }
          });
          
          // Também chamar trigger para garantir que o React Hook Form atualize
          await trigger(undefined, { shouldFocus: false });
          return false;
        }
        
        // Se passou na validação, limpar erros e validar com React Hook Form
        clearErrors();
        
        const result = await trigger(undefined, { shouldFocus: false });
        return result;
      } catch {
        // Se houver erro na validação, retornar false
        return false;
      }
    });
  }, [trigger, registerCustomerInfoValidation, setValue, setError, clearErrors, state.customerInfo]);

  return (
    <Card id="customer-info" className="border shadow-none">
      <CardContent className="p-6">
        <form>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 sm:col-span-1">
              <ControlledInput
                control={control}
                name="firstName"
                label="Primeiro nome"
                type="text"
                placeholder="Maria"
              />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <ControlledInput
                control={control}
                name="lastName"
                label="Sobrenome"
                type="text"
                placeholder="Silva"
              />
            </div>
            <div className="col-span-2">
              <ControlledInput
                control={control}
                name="email"
                label="E-mail"
                type="email"
                placeholder="nome@exemplo.com"
              />
            </div>
            <div className="col-span-2">
              <ControlledInput
                control={control}
                name="phone"
                label="Telefone"
                type="tel"
                placeholder="(11) 99999-9999"
                hint="Incluir código de área com DDD"
              />
            </div>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};
